import { baseApi } from "@/core/api/baseApi";

import type {
  GroupDTO,
  MemberDTO,
  MembersListResponse,
  InviteMemberPayload,
  UpdateMemberPayload,
} from "../../domain/dto/MemberGroupDTO";
import type { UserPermissionsResponseDTO } from "@/features/profile/domain/dto/ProfileDTO";
import {
  mapUserPermissionsResponse,
  type UserPermissionsResult,
} from "@/features/profile/infrastructure/api/userPermissionsResponse";

import { mapGroupsDTO, mapMembersDTO, mapGroupDTO } from "./mapper";
import type {
  Group,
  Member,
  MemberExistResponse,
} from "../../domain/entities/Member";
import type { Designations } from "@/features/auth/domain/entities/Designations";

export const membersGroupsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ GET ALL HOSPITAL MEMBERS
    getMembers: builder.query<
      Member[],
      {
        query?: string;
        group_ids?: string[];
        role_ids?: string[];
        page?: number;
        pageSize?: number;
      } | void
    >({
      query: (args = {}) => {
        const {
          query,
          group_ids,
          role_ids,
          page = 1,
          pageSize = 10,
        } = args ?? {};
        const qs = new URLSearchParams();

        if (query) qs.append("query", query);
        group_ids?.forEach((groupId) => qs.append("group_ids", groupId));
        role_ids?.forEach((roleId) => qs.append("role_ids", roleId));
        qs.append("page", page.toString());
        qs.append("page_size", pageSize.toString());

        return {
          url: `/user_institute/member/list/?${qs.toString()}`,
        };
      },
      transformResponse: (response: { data: MembersListResponse }) =>
        mapMembersDTO(response.data?.members ?? []),
      providesTags: ["Members"],
    }),

    // ✅ GET DOCTORS AND INSTITUTE ADMINS ELIGIBLE FOR SCHEDULING
    getDoctors: builder.query<
      Member[],
      {
        query?: string;
        group_ids?: string[];
      } | void
    >({
      query: (args = {}) => {
        const {
          query,
          group_ids,
        } = args ?? {};
        const qs = new URLSearchParams();

        if (query) qs.append("query", query);
        group_ids?.forEach((groupId) => qs.append("group_ids", groupId));

        return {
          url: `/user_institute/member/doctors`,
          params: qs,
        };
      },
      transformResponse: (response: { data: MembersListResponse }) =>
        mapMembersDTO(response.data?.members ?? []),
      providesTags: ["Members"],
    }),

    // ✅ INVITE MEMBER
    inviteMember: builder.mutation<void, InviteMemberPayload>({
      query: (body) => ({
        url: `/user_institute/member/add/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Members"],
    }),

    // ✅ UPDATE MEMBER
    updateMember: builder.mutation<void, UpdateMemberPayload>({
      query: ({ user_id, ...body }) => ({
        url: `/user_institute/member/${user_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Members"],
    }),

    // ✅ GET DESIGNATIONS
    getDesignations: builder.query<Designations[], void>({
      query: () => `/designations`,
      transformResponse: (response: {
        data: { designations: Designations[] };
      }) => response.data.designations ?? [],
    }),

    // ✅ GET ALL GROUPS
    getGroups: builder.query<
      Group[],
      { query?: string; page?: number; pageSize?: number } | void
    >({
      query: (args = {}) => {
        const { query, page, pageSize } = args ?? {};
        return {
          url: `/hospital-groups`,
          params: {
            ...(query ? { query } : {}),
            ...(page ? { page } : {}),
            ...(pageSize ? { page_size: pageSize } : {}),
          },
        };
      },
      transformResponse: (response: { data: GroupDTO[] }) =>
        mapGroupsDTO(response.data ?? []),
      providesTags: ["Groups"],
    }),

    // ✅ GET GROUP MEMBERS
    getGroupMembers: builder.query<
      Member[],
      { groupId: string; query?: string; page?: number; pageSize?: number }
    >({
      query: ({ groupId, query, page = 1, pageSize = 100 }) => ({
        url: `/hospital-groups/${groupId}/members`,
        params: { query, page, page_size: pageSize },
      }),
      transformResponse: (response: {
        data?: MemberDTO[] | MembersListResponse;
      }) => {
        const data = response?.data;
        const members = Array.isArray(data) ? data : (data?.members ?? []);
        return mapMembersDTO(members);
      },
      providesTags: ["Members"],
    }),

    // ✅ ADD MEMBER TO GROUP
    addGroupMember: builder.mutation<void, { groupId: string; userId: string }>(
      {
        query: ({ groupId, userId }) => ({
          url: `/hospital-groups/${groupId}/members`,
          method: "POST",
          body: { user_id: userId },
        }),
        invalidatesTags: ["Members", "Groups"],
      },
    ),

    // ✅ REMOVE MEMBER FROM GROUP
    removeGroupMember: builder.mutation<
      void,
      { groupId: string; memberUserId: string }
    >({
      query: ({ groupId, memberUserId }) => ({
        url: `/hospital-groups/${groupId}/members/${memberUserId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Members", "Groups"],
    }),

    // ✅ CREATE GROUP
    createGroup: builder.mutation<
      Group,
      { name: string; description?: string }
    >({
      query: (body) => ({
        url: `/hospital-groups`,
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: GroupDTO }) =>
        mapGroupDTO(response.data),
      invalidatesTags: ["Groups"],
    }),

    // ✅ UPDATE GROUP
    updateGroup: builder.mutation<
      Group,
      { groupId: string; name: string; description?: string }
    >({
      query: ({ groupId, ...body }) => ({
        url: `/hospital-groups/${groupId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: { data: GroupDTO }) =>
        mapGroupDTO(response.data),
      invalidatesTags: ["Groups"],
    }),

    // ✅ DELETE GROUP
    deleteGroup: builder.mutation<void, string>({
      query: (groupId) => ({
        url: `/hospital-groups/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Groups"],
    }),

    // ✅ DEACTIVATE MEMBER
    deactivateMember: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/user_institute/member/${userId}/deactivate`,
        method: "PUT",
      }),
      invalidatesTags: ["Members"],
    }),

    // ✅ ACTIVATE MEMBER
    activateMember: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/user_institute/member/${userId}/activate`,
        method: "PUT",
      }),
      invalidatesTags: ["Members"],
    }),

    // ✅ DELETE MEMBER
    deleteMember: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/user_institute/member/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Members"],
    }),

    // ✅ GET GROUPS BY USER
    getUserGroups: builder.query<{ id: string; name: string }[], string>({
      query: (userId) => ({
        url: `/hospital-groups/users/${userId}`,
        params: { page: 1, page_size: 100 },
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) =>
        (response?.data ?? []).map((g: any) => ({ id: g.id, name: g.name })),
      providesTags: ["Groups"],
    }),

    // ✅ GET USER PERMISSIONS
    getUserPermissions: builder.query<UserPermissionsResult, string>({
      query: (userId) => `/roles-permissions/permissions/${userId}`,
      transformResponse: (response: UserPermissionsResponseDTO) =>
        mapUserPermissionsResponse(response),
      providesTags: (_result, _error, userId) => [
        { type: "Permissions", id: userId },
      ],
    }),

    checkMemberExist: builder.query<
      { exists: boolean; message?: string },
      {
        email?: string;
        phone?: string;
        exclude_user_id?: string;
      }
    >({
      query: ({ email, phone, exclude_user_id }) => ({
        url: "/user_institute/member/exist",
        params: {
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          ...(exclude_user_id ? { exclude_user_id } : {}),
        },
      }),
      transformResponse: (response: MemberExistResponse) => ({
        exists: response.data?.exists ?? false,
        message: response.data?.message,
      }),
    }),

    // ✅ UPDATE USER PERMISSION OVERRIDES
    updateUserPermissions: builder.mutation<
      void,
      {
        userId: string;
        grant_permission_ids: string[];
        revoke_permission_ids: string[];
      }
    >({
      query: ({ userId, grant_permission_ids, revoke_permission_ids }) => ({
        url: `/roles-permissions/permissions/${userId}/overrides`,
        method: "PUT",
        body: {
          grant_permission_ids,
          revoke_permission_ids,
        },
      }),
      invalidatesTags: ["Permissions"],
    }),
  }),
});

export const {
  useGetMembersQuery,
  useGetDoctorsQuery,
  useInviteMemberMutation,
  useUpdateMemberMutation,
  useGetDesignationsQuery,
  useGetGroupsQuery,
  useGetGroupMembersQuery,
  useLazyGetGroupMembersQuery,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,
  useDeactivateMemberMutation,
  useActivateMemberMutation,
  useDeleteMemberMutation,
  useGetUserGroupsQuery,
  useLazyGetUserGroupsQuery,
  useGetUserPermissionsQuery,
  useUpdateUserPermissionsMutation,
  useLazyCheckMemberExistQuery,
} = membersGroupsApi;
